<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="services-modal"
    :title="edited.id > 0 ? 'Edit service details' : 'Add new service'"
    ok-title="Save"
    cancel-variant="outline-secondary"
    :ok-disabled="isFormLoading"
    :cancel-disabled="isFormLoading"
    @ok.prevent="onSubmit"
  >
    <div
      v-if="isFormLoading"
      class="modal-overlay d-flex align-items-center justify-content-center"
    >
      <b-spinner
        variant="primary"
        label="Loading..."
      />
    </div>

    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Name"
            label-for="service-name"
          >
            <b-form-input
              id="service-name"
              v-model="edited.name"
              placeholder="Name"
            />
            <small
              v-if="v$.edited.name.$error"
              class="text-danger"
            >Name is required</small>
          </b-form-group>

          <b-form-group
            label="Description"
            label-for="service-description"
          >
            <b-form-textarea
              id="service-description"
              v-model="edited.description"
              placeholder="Description"
              rows="3"
              max-rows="6"
            />
          </b-form-group>

          <b-form-group
            label="Pictures"
            label-for="service-picture"
          >
            <b-form-file
              id="service-picture"
              v-model="edited.picture"
              multiple="multiple"
              placeholder="Choose the files or drop them here..."
              drop-placeholder="Drop the files here..."
              class="mb-1"
            />
            <div class="d-flex flex-wrap">
              <div
                v-for="(pic, index) in edited.picture_preview || []"
                :key="`svc-pic-${index}`"
                class="unit-image-edit-container"
              >
                <div class="unit-image-delete-container">
                  <b-button
                    variant="primary"
                    class="mx-0 float-right"
                    size="sm"
                    @click="deleteImage(pic)"
                  >
                    <span>{{ isImageInDeleteArray(pic.src) ? 'Restore' : 'X' }}</span>
                  </b-button>
                </div>
                <img
                  :src="pic.src"
                  :style="{ width: '150px', border: '5px solid #DDDDDD' }"
                  :class="'my-1 mx-1 ' + (isImageInDeleteArray(pic.src) ? 'img-transparent' : '')"
                  alt=""
                >
              </div>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <b-form-group
            :label="`Price (${currencySymbol})`"
            label-for="service-price"
          >
            <b-form-input
              id="service-price"
              v-model="edited.price"
              placeholder="Price"
              type="number"
            />
            <small
              v-if="v$.edited.price.$error"
              class="text-danger"
            >Price is required</small>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group
            label="Service category"
            label-for="service-category"
          >
            <b-form-select
              id="service-category"
              v-model="edited.service_category_id"
            >
              <b-form-select-option :value="null">
                None
              </b-form-select-option>
              <b-form-select-option
                v-for="category in serviceCategories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script setup>
import { computed, inject, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  BButton,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import useVuelidate from '@vuelidate/core'
import { minLength, numeric, required } from '@vuelidate/validators'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import { ORDER_SERVICE_ADD_KEY } from '@/constants/injectionKeys'

const cloneObject = (object) => JSON.parse(JSON.stringify(object))

const defaultService = {
  id: null,
  name: null,
  description: null,
  picture: null,
  picture_preview: null,
  price: null,
  service_category_id: null,
}

const props = defineProps({
  service: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    default: null,
  },
})

const emit = defineEmits(['service-created'])
const selectOrderService = inject(ORDER_SERVICE_ADD_KEY, null)

const store = useStore()
const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)
const imagesToDelete = ref([])
const edited = reactive(cloneObject(defaultService))
const serviceCategories = computed(() => store.state.serviceCategories.serviceCategories || [])
const currencySymbol = computed(() => window.currency_symbol || '$')

const notifyServiceCreated = (createdService) => {
  const service = createdService?.data ?? createdService

  if (!service?.id) {
    return
  }

  store.commit('services/SET_ORDER_CREATED_SERVICE', service)
  emit('service-created', createdService)

  if (typeof selectOrderService === 'function') {
    selectOrderService(createdService)
  }
}

const rules = {
  edited: {
    name: { required, minLength: minLength(2) },
    price: { required, numeric },
  },
}
const v$ = useVuelidate(rules, { edited })

watch(
  () => store.state.services.service,
  (payload) => {
    const data = payload?.data || payload
    if (data && data.id > 0) {
      edited.id = data.id
      edited.name = data.name
      edited.description = data.description
      edited.picture_preview = data.picture
      edited.price = data.price
      edited.service_category_id = data.service_category_id
      imagesToDelete.value = []
    } else {
      Object.assign(edited, cloneObject(defaultService))
      imagesToDelete.value = []
    }
  },
)

watch(
  () => props.clickDate,
  async () => {
    if (!props.clickDate) {
      return
    }

    isContentLoading.value = true
    try {
      await store.dispatch('serviceCategories/loadServiceCategories')
      if (props.service !== null) {
        await store.dispatch('services/loadService', props.service.id)
      } else {
        store.commit('services/SET_SERVICE', null)
        Object.assign(edited, cloneObject(defaultService))
        imagesToDelete.value = []
      }
      isContentLoading.value = false
      showModal.value = true
    } catch (error) {
      isContentLoading.value = false
      showModal.value = false
      toastFailure(`Something went wrong: ${error.message}`)
    }
  },
)

const deleteImage = (img) => {
  const index = imagesToDelete.value.indexOf(img.src)
  if (index > -1) {
    imagesToDelete.value.splice(index, 1)
  } else {
    imagesToDelete.value.push(img.src)
  }
}

const isImageInDeleteArray = (imgSrc) => imagesToDelete.value.includes(imgSrc)

const onSubmit = async (event) => {
  event.preventDefault()
  const isFormCorrect = await v$.value.$validate()
  if (!isFormCorrect) {
    return
  }
  await submitForm()
}

const submitForm = async () => {
  isFormLoading.value = true
  const formData = new FormData()

  formData.append('name', edited.name ?? '')
  formData.append('description', edited.description ?? '')
  formData.append('price', edited.price ?? '')
  formData.append('service_category_id', edited.service_category_id ?? 0)

  if (edited.picture && edited.picture.length) {
    Array.from(edited.picture).forEach((file) => {
      formData.append('picture[]', file)
    })
  }

  imagesToDelete.value.forEach((image) => {
    formData.append('pictures_to_delete[]', image)
  })

  try {
    if (edited.id != null) {
      formData.append('id', edited.id)
      formData.append('_method', 'PUT')
      await store.dispatch('services/updateService', { formData })
      toastSuccess('Successfully updated')
    } else {
      const created = await store.dispatch('services/addService', { formData })
      notifyServiceCreated(created)
      toastSuccess('Successfully added')
    }
    showModal.value = false
  } catch (error) {
    toastFailure(backendErrorString(error) || 'Failed to save')
  } finally {
    isFormLoading.value = false
    edited.picture = null
  }
}
</script>

<style scoped>
.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  z-index: 10;
}

.img-transparent {
  opacity: 0.35;
}
</style>
